import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, TextField, MenuItem, Button, Typography, Alert,
  Checkbox, FormControlLabel, Tooltip, Box,
  useMediaQuery, useTheme
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { getHolidays } from '../../api';
import { useTerms } from '../../context/TerminologyContext';

export default function TimeEntryFormDialog({
  open, onClose, onSubmit,
  editMode, formData, formErrors, handleInputChange, setFormData,
  canFilterEmployees, employees,
  companies, affiliates, departments, departmentsEnabled,
  isAdmin, canManagePaid, canEditTimes = true
}) {
  const { t } = useTerms();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [holidayNames, setHolidayNames] = useState([]);
  const selectedEmployee = (employees || []).find(e => String(e.id) === String(formData?.employee_id));
  const isSalaried = selectedEmployee?.employee_type === 'salary';
  const salaryCovered = formData?.pay_basis === 'salary';

  // Surface "this date is a holiday" for the selected employee's schedule
  useEffect(() => {
    let cancelled = false;
    const date = formData?.entry_date;
    if (!open || !date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setHolidayNames([]);
      return undefined;
    }
    const params = { start_date: date, end_date: date };
    if (canFilterEmployees && formData.employee_id) params.employee_id = formData.employee_id;
    getHolidays(params)
      .then(res => { if (!cancelled) setHolidayNames(res.data || []); })
      .catch(() => { if (!cancelled) setHolidayNames([]); });
    return () => { cancelled = true; };
  }, [open, formData?.entry_date, formData?.employee_id, canFilterEmployees]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="entry-dialog-title"
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          onSubmit();
        }
      }}
    >
      <DialogTitle id="entry-dialog-title">{editMode ? 'Edit Entry' : 'Add New Entry'}</DialogTitle>
      <DialogContent>
        {editMode && !canEditTimes && (
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            Contact a manager to update times on your time entry.
          </Alert>
        )}
        {holidayNames.length > 0 && (
          <Alert severity="info" sx={{ mb: 1, mt: 1 }}>
            This date is a holiday: <strong>{holidayNames.map(h => h.name).join(', ')}</strong>
            {/* Premium note only when the resolved schedule is the selected
                employee's (an unselected-employee fetch resolves the tenant
                default, whose multiplier may not match) */}
            {holidayNames[0].worked_holiday_multiplier > 1 && (!canFilterEmployees || formData.employee_id) && (
              <> — a <strong>×{Number(holidayNames[0].worked_holiday_multiplier).toFixed(2)}</strong> holiday pay rate will be applied on save</>
            )}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {canFilterEmployees && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                required
                label="Employee"
                name="employee_id"
                value={formData.employee_id}
                onChange={handleInputChange}
                size={isMobile ? "small" : "medium"}
              >
                {employees.map(emp => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} sm={canFilterEmployees ? 6 : 12}>
            <TextField
              fullWidth
              type="date"
              required
              label="Date"
              name="entry_date"
              value={formData.entry_date}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              size={isMobile ? "small" : "medium"}
              disabled={editMode && !canEditTimes}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label={t('client.singular')}
              name="company_id"
              value={formData.company_id}
              onChange={handleInputChange}
              size={isMobile ? "small" : "medium"}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">None</MenuItem>
              {companies.map(comp => (
                <MenuItem key={comp.id} value={comp.id}>
                  {comp.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label={t('affiliate.singular')}
              name="affiliate_id"
              value={formData.affiliate_id}
              onChange={handleInputChange}
              size={isMobile ? "small" : "medium"}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">None</MenuItem>
              {affiliates
                // Affiliates are always client-scoped (migration 084). With
                // no client picked we show all so the user can still browse;
                // with a client picked we narrow to just that client's.
                .filter(aff =>
                  !formData.company_id ||
                  String(aff.company_id) === String(formData.company_id)
                )
                .map(aff => (
                  <MenuItem key={aff.id} value={aff.id}>
                    {aff.name}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
          {departmentsEnabled && departments.length > 0 && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label={t('department.singular')}
              name="department_id"
              value={formData.department_id}
              onChange={handleInputChange}
              size={isMobile ? "small" : "medium"}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">None</MenuItem>
              {departments.map(dept => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          )}
          {isAdmin && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Entry Type"
                name="entry_type"
                value={formData.entry_type || 'regular'}
                onChange={handleInputChange}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="regular">Regular</MenuItem>
                <MenuItem value="bonus">Bonus</MenuItem>
              </TextField>
            </Grid>
          )}
          {isAdmin && isSalaried && formData.entry_type !== 'bonus' && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextField
                  fullWidth
                  select
                  label="Pay Basis"
                  name="pay_basis"
                  value={formData.pay_basis || ''}
                  onChange={handleInputChange}
                  size={isMobile ? 'small' : 'medium'}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="">Automatic</MenuItem>
                  <MenuItem value="hourly">Hourly — paid at the rate</MenuItem>
                  <MenuItem value="salary">Salary-covered — not paid separately</MenuItem>
                </TextField>
                <Tooltip title="Salary-covered: the hours are billed to the client but the employee's salary already covers them, so the entry carries no pay. Hourly: paid at the Hourly Rate on top of salary. Automatic uses the client's Override Pay Rate for this employee (0 = Salary-covered), otherwise Hourly.">
                  <InfoOutlined fontSize="small" color="action" />
                </Tooltip>
              </Box>
            </Grid>
          )}
          {formData.entry_type === 'bonus' ? (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                required
                label="Bonus Amount"
                name="bonus_amount"
                value={formData.bonus_amount || ''}
                onChange={handleInputChange}
                InputProps={{ startAdornment: '$' }}
                inputProps={{ step: '0.01', min: 0.01 }}
                size={isMobile ? "small" : "medium"}
                helperText="Dollar amount added to paycheck"
              />
            </Grid>
          ) : (
            <>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  type="time"
                  required
                  label="Start Time"
                  name="start_time"
                  value={formData.start_time}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  size={isMobile ? "small" : "medium"}
                  error={!!formErrors.start_time}
                  helperText={formErrors.start_time}
                  disabled={editMode && !canEditTimes}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  type="time"
                  required
                  label="End Time"
                  name="end_time"
                  value={formData.end_time}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  size={isMobile ? "small" : "medium"}
                  error={!!formErrors.end_time}
                  helperText={formErrors.end_time}
                  disabled={editMode && !canEditTimes}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Break (minutes)"
                  name="break_minutes"
                  value={formData.break_minutes}
                  onChange={handleInputChange}
                  error={!!formErrors.break_minutes}
                  helperText={formErrors.break_minutes}
                  inputProps={{ min: 0 }}
                  size={isMobile ? "small" : "medium"}
                  disabled={editMode && !canEditTimes}
                />
              </Grid>
            </>
          )}
          {isAdmin && formData.entry_type !== 'bonus' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                required
                label="Hourly Rate"
                name="hourly_rate"
                value={salaryCovered ? 0 : formData.hourly_rate}
                onChange={handleInputChange}
                InputProps={{ startAdornment: '$' }}
                inputProps={{ step: '0.01', min: 0.01 }}
                size={isMobile ? "small" : "medium"}
                disabled={salaryCovered}
                error={!!formErrors.hourly_rate}
                helperText={formErrors.hourly_rate || (salaryCovered ? 'Covered by salary — not paid' : undefined)}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              required={formData.billable !== false}
              label="Description"
              name="description"
              value={formData.description || ''}
              onChange={handleInputChange}
              placeholder={`Short summary of the work (appears on the ${t('client.singular', { lower: true })}'s invoice)`}
              helperText={formErrors.description || `Shown on the ${t('client.singular', { lower: true })} invoice. Required for billable entries.`}
              error={!!formErrors.description}
              size={isMobile ? "small" : "medium"}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Work Details"
              name="work_details"
              value={formData.work_details || ''}
              onChange={handleInputChange}
              placeholder="Internal notes — longer work context, visible to you and your team (not on the invoice)"
              size={isMobile ? "small" : "medium"}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.billable}
                  onChange={(e) => setFormData(prev => ({ ...prev, billable: e.target.checked }))}
                  size="small"
                />
              }
              label={<Typography variant="body2">Billable</Typography>}
            />
          </Grid>
          {canManagePaid && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Manager Notes"
                name="manager_notes"
                value={formData.manager_notes}
                onChange={handleInputChange}
                placeholder="Internal notes visible only to managers and admins"
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 152, 0, 0.05)'
                  }
                }}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} size={isMobile ? "medium" : "large"}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          size={isMobile ? "medium" : "large"}
        >
          {editMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
